import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/shared';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingLayoutProps {
  children: ReactNode;
  showHelp?: boolean;
}

/**
 * Onboarding Layout Wrapper
 *
 * Provides consistent header and footer for onboarding wizard steps.
 * Follows the same design patterns as AuthLayout but adapted for
 * authenticated onboarding flow.
 *
 * Features:
 * - Left-aligned logo (consistent with auth pages)
 * - Help button in header
 * - User email display with logout option
 * - Footer with copyright
 */
export function OnboardingLayout({
  children,
  showHelp = true,
}: OnboardingLayoutProps) {
  const { t } = useTranslation('onboarding');
  const { changeLanguage, currentLanguage } = useLanguage();
  const { signOut, user } = useAuth();
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background dark:bg-[#121520]">
      <div className="layout-container flex h-full grow flex-col">
        {/* Header - Responsive: Mobile (compact) → Tablet → Desktop */}
        <header className="flex items-center justify-between border-b border-solid border-slate-200 dark:border-slate-800 px-3 sm:px-4 md:px-6 lg:px-20 xl:px-40 py-3 md:py-4 bg-surface dark:bg-[#121520]">
          {/* Logo - Responsive sizing */}
          <Link
            to={ROUTES.DASHBOARD}
            className="flex items-center gap-2 md:gap-2.5 lg:gap-3 text-primary flex-shrink-0"
          >
            <div className="size-7 md:size-8 lg:size-9 flex items-center justify-center bg-primary/10 rounded-md lg:rounded-lg">
              <span className="material-symbols-outlined text-primary text-xl md:text-2xl">
                account_tree
              </span>
            </div>
            <h2 className="text-slate-900 dark:text-slate-100 text-sm sm:text-base md:text-lg lg:text-xl font-bold leading-tight tracking-[-0.015em]">
              <span className="hidden sm:inline">FamilyTreeBuilder</span>
              <span className="sm:hidden">FTB</span>
            </h2>
          </Link>

          {/* Right side - Language Switcher, Help button and user menu - Responsive spacing */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 flex-shrink-0">
            {/* User email - responsive with better width on tablets */}
            {user?.email && (
              <span className="hidden md:inline text-xs md:text-sm text-slate-500 dark:text-slate-400 truncate max-w-[150px] lg:max-w-[180px] xl:max-w-[250px]">
                {user.email}
              </span>
            )}

            {/* Language Switcher - Compact on mobile */}
            <div className="flex items-center gap-1 sm:gap-1.5 lg:gap-2 border border-primary/20 rounded-full p-0.5 lg:p-1">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
                  currentLanguage === 'en'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:text-primary'
                }`}
                aria-label="Switch to English"
                title="English"
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`px-2 sm:px-2.5 lg:px-3 py-0.5 lg:py-1 text-[10px] sm:text-xs font-semibold rounded-full transition-colors ${
                  currentLanguage === 'ar'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:text-primary'
                }`}
                aria-label="Switch to Arabic"
                title="العربية"
              >
                ع
              </button>
            </div>

            {/* Help/Info button - Extra small on mobile phones */}
            {showHelp && (
              <button
                onClick={() => setIsInfoDialogOpen(true)}
                className="flex items-center justify-center rounded-full size-6 sm:size-7 md:size-8 lg:size-9 xl:size-10 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-primary/10 hover:text-primary transition-colors flex-shrink-0"
                aria-label={t('header.help', 'Help')}
                title={t('header.help', 'Help')}
              >
                <span className="material-symbols-outlined text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">info</span>
              </button>
            )}

            {/* Logout button - Extra small on mobile phones */}
            <button
              onClick={signOut}
              className="flex items-center gap-0.5 sm:gap-1 md:gap-1.5 lg:gap-2 px-1 sm:px-1.5 md:px-2 lg:px-3 xl:px-4 py-0.5 sm:py-1 md:py-1.5 lg:py-2 rounded-md lg:rounded-lg text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
              aria-label={t('header.logout', 'Logout')}
            >
              <span className="material-symbols-outlined text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">logout</span>
              <span className="hidden sm:inline">{t('header.logout', 'Logout')}</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        {children}

        {/* Footer - Responsive spacing */}
        <footer className="px-3 sm:px-4 md:px-6 py-4 sm:py-5 md:py-6 text-center text-slate-500 dark:text-slate-400 text-xs sm:text-sm border-t border-primary/5">
          <p className="text-[10px] sm:text-xs md:text-sm">
            {t('footer.copyright', '© 2026 FamilyTreeBuilder. Preserving heritage for generations.')}
          </p>
        </footer>
      </div>

      {/* Info Dialog - Responsive Modal */}
      {isInfoDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setIsInfoDialogOpen(false)}
        >
          <div
            className="relative bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-sm sm:max-w-md md:max-w-lg animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 md:p-6 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center justify-center size-8 sm:size-10 bg-primary/10 rounded-lg">
                  <span className="material-symbols-outlined text-primary text-lg sm:text-xl md:text-2xl">info</span>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 dark:text-slate-100">
                  {t('infoDialog.title', 'About Onboarding')}
                </h3>
              </div>
              <button
                onClick={() => setIsInfoDialogOpen(false)}
                className="flex items-center justify-center size-8 sm:size-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Close"
              >
                <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-lg sm:text-xl">close</span>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {t(
                  'infoDialog.description',
                  'Welcome to your family tree journey! These simple steps will guide you through building a comprehensive family tree.'
                )}
              </p>

              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {t('infoDialog.stepsTitle', 'What you\'ll add:')}
                </h4>
                <ul className="space-y-1.5 sm:space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-base sm:text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                      {t('infoDialog.step1', 'Your parents\' information (father and mother)')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-base sm:text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                      {t('infoDialog.step2', 'Your paternal grandparents (father\'s parents)')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-base sm:text-lg flex-shrink-0 mt-0.5">check_circle</span>
                    <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                      {t('infoDialog.step3', 'Your maternal grandparents (mother\'s parents)')}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="pt-2 sm:pt-3 border-t border-slate-200 dark:border-slate-800">
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 italic">
                  {t(
                    'infoDialog.note',
                    'Don\'t worry - you can always update or add more information later!'
                  )}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 md:p-6 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setIsInfoDialogOpen(false)}
                className="w-full px-4 py-2 sm:py-2.5 md:py-3 bg-primary text-white text-xs sm:text-sm md:text-base font-semibold rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('infoDialog.gotIt', 'Got it, thanks!')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
